import React, { useState, useMemo } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import type {
  QualityPolicyManagerProps,
  QualityPolicyData,
  QualityThresholdConfig,
  PolicyEnforcementRule,
  QualityPolicyTemplate
} from '../../types/advanced_content_quality_ui';

// Policy category styling
const POLICY_CATEGORY_STYLES = {
  quality: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800', icon: '📊' },
  compliance: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800', icon: '✅' },
  brand: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-800', icon: '🎨' },
  legal: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-800', icon: '⚖️' },
  operational: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800', icon: '⚙️' },
  custom: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-800', icon: '🔧' }
};

// Enforcement action styling
const ENFORCEMENT_STYLES = {
  warn: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: '⚠️' },
  block: { bg: 'bg-red-100', text: 'text-red-800', icon: '🚫' },
  auto_correct: { bg: 'bg-blue-100', text: 'text-blue-800', icon: '🔧' },
  escalate: { bg: 'bg-orange-100', text: 'text-orange-800', icon: '📈' }
};

export const QualityPolicyManager: React.FC<QualityPolicyManagerProps> = ({
  policyData,
  editMode = false,
  showTemplates = true,
  onPolicyUpdate,
  onThresholdChange
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'thresholds' | 'standards' | 'enforcement' | 'templates'>('overview');
  const [isEditing, setIsEditing] = useState(editMode);
  const [updatingPolicy, setUpdatingPolicy] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [thresholds, setThresholds] = useState(policyData.qualityThresholds);

  // Calculate policy statistics
  const policyStats = useMemo(() => {
    const { policyEnforcement, policyTemplates } = policyData;
    
    return {
      totalPolicies: policyEnforcement.enforcementRules.length,
      activePolicies: policyEnforcement.enforcementRules.filter(rule => rule.enabled).length,
      violationRate: policyEnforcement.violationTracking.complianceRate,
      avgResolutionTime: policyEnforcement.enforcementMetrics.resolutionTime,
      templates: policyTemplates.length
    };
  }, [policyData]);

  // Handle threshold updates
  const handleThresholdUpdate = (metric: string, value: number) => {
    const updatedThresholds = {
      ...thresholds,
      minimumScores: {
        ...thresholds.minimumScores,
        [metric]: value
      }
    };
    
    setThresholds(updatedThresholds);
    onThresholdChange?.(updatedThresholds);
  };

  // Handle policy save
  const handleSavePolicy = async () => {
    if (!onPolicyUpdate) return;

    setUpdatingPolicy(true);
    try {
      await onPolicyUpdate({ ...policyData, qualityThresholds: thresholds });
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating policy:', error);
    } finally {
      setUpdatingPolicy(false);
    }
  };

  // Render threshold configuration
  const renderThresholdConfig = () => {
    const { minimumScores } = thresholds;
    
    return (
      <div className="space-y-6">
        {/* Quality Score Thresholds */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quality Score Thresholds</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(minimumScores).map(([metric, value]) => (
              <div key={metric} className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 capitalize">
                  {metric.replace(/([A-Z])/g, ' $1').toLowerCase()}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={value}
                    onChange={(e) => handleThresholdUpdate(metric, Number(e.target.value))}
                    disabled={!isEditing}
                    className={`w-full px-3 py-2 border rounded-lg text-sm ${
                      isEditing 
                        ? 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent' 
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  />
                  <span className="absolute right-3 top-2 text-gray-500 text-sm">%</span>
                </div>
                <div className="text-xs text-gray-500">
                  Current: {value}% minimum
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Escalation Rules */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Escalation Rules</h3>
          <div className="space-y-3">
            {thresholds.escalationRules.map((rule, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{rule.condition}</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    rule.escalationLevel === 1 ? 'bg-yellow-100 text-yellow-800' :
                    rule.escalationLevel === 2 ? 'bg-orange-100 text-orange-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    Level {rule.escalationLevel}
                  </span>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <div>Assignee: {rule.assignee}</div>
                  <div>Timeout: {rule.timeoutMinutes} minutes</div>
                  <div>Actions: {rule.actions.join(', ')}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Custom Thresholds */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Custom Thresholds</h3>
          <div className="space-y-4">
            {thresholds.customThresholds.map((threshold, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{threshold.name}</h4>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    threshold.priority === 1 ? 'bg-red-100 text-red-800' :
                    threshold.priority === 2 ? 'bg-orange-100 text-orange-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    Priority {threshold.priority}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-3">{threshold.description}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Clarity:</span>
                    <span className="ml-1 font-medium">{threshold.thresholds.clarity}%</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Coherence:</span>
                    <span className="ml-1 font-medium">{threshold.thresholds.coherence}%</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Impact:</span>
                    <span className="ml-1 font-medium">{threshold.thresholds.impact}%</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Overall:</span>
                    <span className="ml-1 font-medium">{threshold.thresholds.overall}%</span>
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  Applies to: {threshold.applicableContentTypes.join(', ')}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  };

  // Render enforcement rules
  const renderEnforcementRules = () => {
    const { enforcementRules, violationTracking, enforcementMetrics } = policyData.policyEnforcement;
    
    return (
      <div className="space-y-6">
        {/* Enforcement Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{enforcementRules.length}</div>
              <div className="text-sm text-gray-600">Active Rules</div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {(violationTracking.complianceRate * 100).toFixed(1)}%
              </div>
              <div className="text-sm text-gray-600">Compliance Rate</div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {enforcementMetrics.resolutionTime.toFixed(0)}m
              </div>
              <div className="text-sm text-gray-600">Avg Resolution</div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {enforcementMetrics.complianceScore.toFixed(1)}
              </div>
              <div className="text-sm text-gray-600">Compliance Score</div>
            </div>
          </Card>
        </div>

        {/* Enforcement Rules */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Enforcement Rules</h3>
          <div className="space-y-4">
            {enforcementRules.map((rule, index) => (
              <div key={rule.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${rule.enabled ? 'bg-green-500' : 'bg-gray-400'}`} />
                    <h4 className="font-medium text-gray-900">{rule.name}</h4>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      ENFORCEMENT_STYLES[rule.enforcement.type].bg
                    } ${ENFORCEMENT_STYLES[rule.enforcement.type].text}`}>
                      {ENFORCEMENT_STYLES[rule.enforcement.type].icon} {rule.enforcement.type.replace('_', ' ')}
                    </span>
                    {isEditing && (
                      <Button size="small" variant="ghost">
                        {rule.enabled ? 'Disable' : 'Enable'}
                      </Button>
                    )}
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 mb-3">{rule.description}</p>
                
                {/* Scope Information */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Content Types:</span>
                    <div className="mt-1">
                      {rule.scope.contentTypes.map((type, typeIndex) => (
                        <span key={typeIndex} className="inline-block mr-1 mb-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">Departments:</span>
                    <div className="mt-1">
                      {rule.scope.departments.map((dept, deptIndex) => (
                        <span key={deptIndex} className="inline-block mr-1 mb-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                          {dept}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">User Roles:</span>
                    <div className="mt-1">
                      {rule.scope.userRoles.map((role, roleIndex) => (
                        <span key={roleIndex} className="inline-block mr-1 mb-1 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">
                          {role}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Monitoring Configuration */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Monitoring: {rule.monitoring.frequency}</span>
                    <span className="text-gray-500">
                      Alerts: {rule.monitoring.alertThresholds.length} thresholds
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Top Violations */}
        {violationTracking.topViolations.length > 0 && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Violations</h3>
            <div className="space-y-3">
              {violationTracking.topViolations.map((violation, index) => (
                <div key={index} className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-red-900">{violation.violationType}</h4>
                    <div className="flex items-center space-x-3">
                      <span className="text-sm text-red-700">Count: {violation.count}</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        violation.trend === 'increasing' ? 'bg-red-200 text-red-900' :
                        violation.trend === 'decreasing' ? 'bg-green-200 text-green-900' :
                        'bg-gray-200 text-gray-900'
                      }`}>
                        {violation.trend}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-red-800 mb-2">{violation.impact}</p>
                  <div className="text-xs text-red-600">
                    Recommended actions: {violation.recommendedActions.join(', ')}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    );
  };

  // Render policy templates
  const renderPolicyTemplates = () => {
    if (!showTemplates) {
      return (
        <Card className="p-8 text-center">
          <span className="text-4xl mb-4 block">📋</span>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Templates Disabled</h3>
          <p className="text-gray-600">Enable templates view to manage policy templates.</p>
        </Card>
      );
    }

    return (
      <div className="space-y-6">
        {/* Template Categories */}
        {Object.entries(
          policyData.policyTemplates.reduce((acc, template) => {
            if (!acc[template.category]) acc[template.category] = [];
            acc[template.category].push(template);
            return acc;
          }, {} as Record<string, QualityPolicyTemplate[]>)
        ).map(([category, templates]) => {
          const categoryStyle = POLICY_CATEGORY_STYLES[category as keyof typeof POLICY_CATEGORY_STYLES];
          
          return (
            <Card key={category} className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <span className="text-xl">{categoryStyle.icon}</span>
                <h3 className="text-lg font-semibold text-gray-900 capitalize">{category} Policies</h3>
                <span className="text-sm text-gray-500">({templates.length} templates)</span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    className={`p-4 rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md ${
                      selectedTemplate === template.id ? 'ring-2 ring-blue-500' : ''
                    } ${categoryStyle.bg} ${categoryStyle.border} border`}
                    onClick={() => setSelectedTemplate(selectedTemplate === template.id ? null : template.id)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className={`font-medium ${categoryStyle.text}`}>{template.name}</h4>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        template.organizationLevel === 'global' ? 'bg-blue-100 text-blue-800' :
                        template.organizationLevel === 'department' ? 'bg-green-100 text-green-800' :
                        template.organizationLevel === 'team' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {template.organizationLevel}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                    
                    <div className="text-xs text-gray-500">
                      Applies to: {template.applicableContentTypes.join(', ')}
                    </div>

                    {/* Expanded Template Details */}
                    {selectedTemplate === template.id && (
                      <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                        <div>
                          <div className="text-sm font-medium text-gray-900 mb-2">Template Structure</div>
                          <div className="space-y-2">
                            {template.template.structure.map((element, index) => (
                              <div key={index} className="text-sm bg-white p-2 rounded border">
                                <span className="font-medium">{element.field}</span>
                                <span className="text-gray-500 ml-2">({element.type})</span>
                                {element.required && (
                                  <span className="ml-2 text-red-600">*</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        {isEditing && (
                          <div className="flex space-x-2 pt-2">
                            <Button size="small" variant="primary">
                              Use Template
                            </Button>
                            <Button size="small" variant="ghost">
                              Edit Template
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          );
        })}
      </div>
    );
  };

  // Tab configuration
  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'thresholds', label: 'Thresholds', icon: '🎯' },
    { id: 'standards', label: 'Standards', icon: '📏' },
    { id: 'enforcement', label: 'Enforcement', icon: '🔒' },
    ...(showTemplates ? [{ id: 'templates', label: 'Templates', icon: '📋' }] : [])
  ] as const;

  return (
    <div className="quality-policy-manager space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Quality Policy Manager</h2>
          <p className="text-gray-600 mt-1">Configure and manage content quality policies</p>
        </div>
        
        <div className="flex items-center space-x-3">
          {!isEditing && (
            <Button
              onClick={() => setIsEditing(true)}
              variant="primary"
            >
              Edit Policies
            </Button>
          )}
          
          {isEditing && (
            <>
              <Button
                onClick={() => setIsEditing(false)}
                variant="ghost"
                disabled={updatingPolicy}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSavePolicy}
                variant="primary"
                disabled={updatingPolicy}
              >
                {updatingPolicy ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">Saving...</span>
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Policy Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{policyStats.totalPolicies}</div>
            <div className="text-sm text-gray-600">Total Policies</div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{policyStats.activePolicies}</div>
            <div className="text-sm text-gray-600">Active Policies</div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {(policyStats.violationRate * 100).toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600">Compliance Rate</div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">
              {policyStats.avgResolutionTime.toFixed(0)}m
            </div>
            <div className="text-sm text-gray-600">Avg Resolution</div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-600">{policyStats.templates}</div>
            <div className="text-sm text-gray-600">Templates</div>
          </div>
        </Card>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Policy Overview</h3>
              <p className="text-gray-600">
                Manage comprehensive quality policies across all content types and organizational levels.
                Configure thresholds, standards, enforcement rules, and utilize templates for consistent policy management.
              </p>
            </Card>
            {renderThresholdConfig()}
          </div>
        )}
        
        {activeTab === 'thresholds' && renderThresholdConfig()}
        {activeTab === 'standards' && renderThresholdConfig()} {/* Reuse for now */}
        {activeTab === 'enforcement' && renderEnforcementRules()}
        {activeTab === 'templates' && renderPolicyTemplates()}
      </div>
    </div>
  );
};

export default QualityPolicyManager;
